import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import authApi from '../../api/auth';
import { LOGIN_COVER } from '../../assets/images';
import  NAV_LOGO  from '../../assets/logos/World_O_Tutor.webp';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { login } from '../../reducers/authSlice';
import { setUserProfile } from '../../reducers/userSlice';
import CompleteProfile from './CompleteProfile';
import EmailScreen from './EmailScreen';
import LicenseTerms from './LicenseTerms';
import { SEO } from '../../components/SEO';

export default function Login() {
  const [step, setStep] = useState<number>(0);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.authSlice);
  const { userData } = useAppSelector((state) => state.userSlice);
  const [isBlocked, setIsBlocked] = useState(false);

  const logout = async () => {
    await authApi.logout().catch((/* err */) => {
      /* console.log(err) */
    });
    dispatch(login(null));
    dispatch(setUserProfile(null));
  };

  useEffect(() => {
    if (location.state) {
      localStorage.setItem('prevPath', location.state.prevPath);
    }
  }, [location]);

  useEffect(() => {
    if (user?._id) {
      if (user.termsOfUse === 'accepted') {
        if (userData) {
          if (userData.status === 'block') {
            logout();
            setStep(0);
            setIsBlocked(true);
            return;
          } else {
            let prevPath = localStorage.getItem('prevPath');
            if (prevPath) {
              navigate(prevPath, { replace: true });
            } else {
              navigate('/dashboard', { replace: true });
            }
          }
        } else {
          setStep(2);
        }
      } else {
        setStep(1);
      }
    } else {
      authApi
        .authStatus()
        .then((data) => {
          if (data.status === 'OK') {
            dispatch(login(data.data));
          }
        })
        .catch((/* err */) => {
          /* console.log(err) */
        });
    }
  }, [user, userData]);

  return (
    <>
    <SEO 
      title="Login - Access Your WorldoTutor Account"
      description="Login to your WorldoTutor account to manage your tutoring sessions, view your dashboard, and connect with tutors."
      keywords="login, sign in, worldotutor account, student login, tutor dashboard"
      url="/login"
      type="website"
      noIndex={true}
      og={{
        type: 'website',
        siteName: 'WorldoTutor',
        locale: 'en_US',
        image: {
          url: '/imgi_1_favicon.png',
          alt: 'Login - WorldoTutor',
          width: 1200,
          height: 630
        }
      }}
      twitter={{
        card: 'summary_large_image',
        site: '@worldotutor',
        image: {
          url: '/imgi_1_favicon.png',
          alt: 'Login - WorldoTutor'
        }
      }}
    />
    <div className="w-full flex flex-col h-screen">
      <div className="hidden md:flex border border-neutralBlue1">
        <Link to="/" className=" h-14 flex items-center w-11/12 mx-auto">
          <img src={NAV_LOGO} alt="WorldOTutor Logo"  className='max-w-44'/>
        </Link>
      </div>
      <div className="w-full h-screen flex lg:flex-row md:justify-center md:bg-neutralGray4">
        <div className="lg:bg-primary lg:w-1/2 lg:h-full lg:flex hidden">
          <div className="w-full h-full flex flex-col items-center justify-center">
            <div className="h-80">
              <img src={LOGIN_COVER} alt="WorldOTutor login cover" className="w-full h-full object-contain" />
            </div>
            <div className="w-1/2">
              <h1 className="text-5xl leading-snug font-bold text-white text-center">Welcome to World O Tutor</h1>
            </div>
          </div>
        </div>
        <div className="flex lg:w-1/2 md:w-4/5 w-full h-full items-center justify-center bg-white md:bg-neutralGray4">
          <div className="flex flex-col py-6 px-4 md:px-6 lg:px-12 w-11/12 lg:w-4/5  min-h-[90%] md:bg-white rounded-xl md:border border-neutralBlue1 md:shadow-md">
            {step === 0 ? (
              <>
                {isBlocked && (
                  <p className="text-base text-red-500 font-semibold my-5">
                    Cannot Login{' '}
                    <Link className="underline" to="/contact">
                      Contact Support
                    </Link>
                  </p>
                )}
                <EmailScreen setStep={setStep} />
              </>
            ) : step === 1 ? (
              <LicenseTerms setStep={setStep} />
            ) : (
              <CompleteProfile setStep={setStep} />
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
